/**
 * Temporary screen-scoped design tokens for the **Settings shell** — the left
 * settings navigation column and the centred content column that every
 * `/settings/*` sub-screen renders into.
 *
 * These mirror the Settings design 1:1 (`Settings.dc.html`). They intentionally
 * live outside `src/theme` for now: once every screen has been re-skinned the
 * shared values get promoted into the global palette/typography and this file
 * is deleted. Components must read from here instead of inlining literals.
 */

import { contentCard, formField, signer, white } from "theme/colors";

export const settingsShellTokens = {
  colors: {
    /** Page canvas behind the nav + content columns. */
    canvas: signer.surface.subtle,
    /** Vertical rule separating nav from content. */
    navBorder: signer.border.light,
    /** "PERSONAL" / "ORGANIZATION" group labels. */
    sectionLabel: signer.text.faint,
    itemText: signer.text.secondary,
    itemTextActive: signer.ink,
    itemBgActive: "#ECEAF2",
    itemBgHover: signer.surface.muted,
    userCardBg: signer.surface.canvas,
    avatarBg: signer.ink,
    avatarText: white,
    userName: signer.ink,
    userEmail: signer.text.faint,
    pageTitle: signer.ink,
  },
  layout: {
    shellMaxWidth: 1360,
    shellPaddingX: 32,
    navWidth: 248,
    navPadding: "24px 16px 16px",
    navPaddingMobile: "16px 0 20px",
    /** Sticky offset = height of the app top nav. */
    navStickyTop: 64,
    contentMaxWidth: 1000,
    contentPadding: "32px 40px 56px",
    contentPaddingMobile: "24px 16px 40px",
    /** Gap between the page title and the header actions (MUI spacing units). */
    headerGap: 2,
  },
  nav: {
    sectionLabelFontSize: "10.5px",
    sectionLabelFontWeight: 700,
    sectionLabelLetterSpacing: "0.12em",
    sectionLabelPadding: "0 12px 8px",
    /** Extra top padding on every group label after the first. */
    sectionLabelPaddingTop: "20px 12px 8px",
    itemGap: "2px",
    itemHeight: 38,
    itemPaddingX: "12px",
    itemRadius: "9px",
    itemFontSize: "13px",
    itemFontWeight: 600,
    itemIconGap: "10px",
    itemIconSize: 16,
    itemIconStrokeWidth: 2,
  },
  userCard: {
    marginTop: "20px",
    radius: "11px",
    padding: "10px 12px",
    gap: "10px",
    avatarSize: 34,
    avatarFontSize: "12px",
    avatarFontWeight: 700,
    nameFontSize: "12.5px",
    nameFontWeight: 700,
    emailFontSize: "10.5px",
    textGap: "1px",
  },
  /** Compact search box in list-screen headers (Roles, Teams, Tickets). */
  search: {
    width: 240,
    height: 40,
    radius: "9px",
    border: formField.border,
    borderWidth: "1px",
    bg: white,
    paddingX: "12px",
    /**
     * MUI paints the outlined border on an *overlaid* `fieldset` instead of
     * insetting it, so it steals no space from the content box. The design's
     * box uses a real 1px border with `box-sizing: border-box`, which pushes
     * its content in by that pixel — add it back here so both land together.
     */
    paddingXCompensated: "13px",
    /**
     * The design's bare `<input>` keeps the browser's default 2px side
     * padding; MUI resets it to 0, which would pull the text 2px left.
     */
    inputPaddingX: "2px",
    gap: "8px",
    iconSize: 13,
    iconStrokeWidth: 2,
    iconColor: signer.text.faint,
    fontSize: "12.5px",
    fontWeight: 500,
    color: signer.ink,
    placeholder: formField.placeholderMuted,
    focusBorder: signer.orange.main,
  },
  pageTitle: {
    fontSize: "22px",
    fontWeight: 800,
    letterSpacing: "-0.01em",
    lineHeight: "normal",
  },
} as const;

/**
 * Raw media query for the narrow phone column of
 * `Settings - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375 and 390 with different gutters, row padding, avatar
 * sizes and type. MUI has no breakpoint between the two, so the split is a raw
 * query applied *after* the `xs` values (which carry the 390 column) so it wins.
 */
export const PHONE_SM_QUERY = "@media (max-width:389.98px)";

/**
 * Phone (`xs`) and tablet (`sm`) geometry for the **Settings shell**, traced
 * from `Settings - Mobile & Tablet.dc.html`.
 *
 * Only values that actually differ from the desktop tokens above live here;
 * anything omitted is inherited at `md`, so the desktop screen is untouched.
 * `sm` / `std` inside `phone` are the reference's 375 and 390 columns.
 */
export const settingsShellResponsiveTokens = {
  phone: {
    /** Index list gutters. */
    indexPadding: { sm: "14px 14px 32px", std: "16px 16px 34px" },
    /** Detail body gutters, used by `SettingsLayout` for every sub-screen. */
    detailPadding: { sm: "16px 14px 40px", std: "18px 16px 42px" },
    /** Gap between stacked content cards on a detail screen. */
    contentGap: { sm: "14px", std: "15px" },

    /** White "who am I" card at the top of the index. */
    userCard: {
      bg: white,
      border: contentCard.border,
      radius: "14px",
      padding: { sm: "13px 15px", std: "14px 16px" },
      gap: { sm: "11px", std: "12px" },
      marginBottom: { sm: "16px", std: "17px" },
      avatarSize: { sm: 40, std: 44 },
      avatarFontSize: { sm: "13.5px", std: "14.5px" },
      nameFontSize: { sm: "13.5px", std: "14px" },
      emailFontSize: { sm: "11px", std: "11.5px" },
    },

    /** "PERSONAL" / "ORGANIZATION" labels above each index group. */
    sectionLabel: {
      fontSize: { sm: "10.5px", std: "11px" },
      letterSpacing: "0.11em",
      padding: "0 4px 8px",
    },
    /** Space below a whole index group. */
    groupMarginBottom: { sm: "16px", std: "17px" },

    /** One tappable index row: icon · label · chevron. */
    row: {
      padding: { sm: "13px 10px", std: "14px 11px" },
      gap: { sm: "12px", std: "13px" },
      radius: "11px",
      iconSize: { sm: 18, std: 19 },
      iconStrokeWidth: 1.9,
      labelFontSize: { sm: "13.5px", std: "14px" },
      labelFontWeight: 600,
      labelColor: signer.ink,
      chevronSize: { sm: 14, std: 15 },
      chevronColor: signer.border.strong,
    },
  },

  tablet: {
    navWidth: 206,
    navPadding: "16px 10px",
    navBorder: signer.border.divider,
    navBg: white,
    contentPadding: "22px 26px 40px",
    contentGap: "16px",
    pageTitleFontSize: "20px",

    nav: {
      sectionLabelFontSize: "10px",
      sectionLabelLetterSpacing: "0.1em",
      sectionLabelPadding: "0 10px 6px",
      sectionLabelPaddingTop: "16px 10px 6px",
      itemPadding: "9px 10px",
      itemRadius: "10px",
      itemFontSize: "13px",
      itemFontWeight: 600,
      itemFontWeightActive: 700,
      itemGap: "11px",
      itemIconSize: 16,
      itemMarginBottom: "1px",
      /** The tablet reference tints the active row orange, not grey. */
      itemColor: signer.text.secondary,
      itemColorActive: signer.orange.main,
      itemBgActive: signer.orange.tint,
    },

    userCard: {
      radius: "11px",
      padding: "9px 10px",
      gap: "9px",
      avatarSize: 30,
      avatarFontSize: "11px",
      nameFontSize: "11.5px",
      emailFontSize: "10px",
    },
  },
} as const;

export default settingsShellTokens;
